/*
  NJ (22 Oct 2026, rules updated 23 Oct 2026): per-order quantity limits, per variant,
  cumulative with the cart.

    In-stock variant .................................. its Shopify stock (inventory 10 -> up to 10)
    Pre-order variant (Continue selling + no stock) ... 4 units
    Sold out (Deny + no stock) ........................ 0 units
    Inventory not tracked by Shopify .................. no limit

  The pre-order rule is the same one snippets/product-form-buttons.liquid and theme.js
  ProductButtonText() use for the "Pre-order Now" button (inventory_policy "continue" and
  inventory_quantity <= 0), so the limit always follows the button the customer sees.
  Cart lines carry the same rule from Liquid (data-nj-qty-max / data-nj-qty-type).

  Used by:
    assets/cart.js        addItemtoCart()  -> guardAdd()      (ATC / Pre-order / quick view / sticky / cards)
                          + / - buttons    -> checkQuantity()
                          changeCartItem() -> guardChange()   (cart drawer + cart page lines)
    assets/theme.js       ProductButtonText() -> refresh()   (variant switch)
    layout/theme.liquid   Breeze Buy Now / cart checkout -> checkQuantity() / cartViolation()

  Every cart mutation that goes through here is serialized and re-reads /cart.js first,
  so rapid clicks or parallel requests cannot slip past the limit.
  This is storefront enforcement only - see the hand-over notes for checkout-level limits.
*/
(function () {
  var PREORDER_LIMIT = 4;
  var MESSAGES = {
    preorder: "Preorder limit reached: You can reserve up to " + PREORDER_LIMIT + " units of this product per order.",
    soldout: "This product is currently out of stock and cannot be purchased.",
    verify: "We couldn't check your cart right now. Please try again.",
  };
  var MSG_SELECTOR = "[data-nj-qty-limit-msg]";
  var inventoryCache = new WeakMap();
  var queue = Promise.resolve();

  /* One cart mutation at a time; each waits for the previous one to finish. */
  function withLock(task) {
    var run = queue.then(task);
    queue = run.catch(function () {});
    return run;
  }

  function getCart() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    return fetch(root + "cart.js", { cache: "no-store", headers: { Accept: "application/json" } }).then(
      function (response) {
        if (!response.ok) throw new Error("cart.js " + response.status);
        return response.json();
      }
    );
  }

  function quantityInCart(cart, variantId, skipIndex) {
    return (cart.items || []).reduce(function (sum, item, index) {
      if (index === skipIndex || String(item.variant_id) !== String(variantId)) return sum;
      return sum + item.quantity;
    }, 0);
  }

  /* ---------- rules: { state: "stock" | "preorder" | "soldout" | "untracked", limit } ---------- */

  function rule(state, limit) {
    return { state: state, limit: limit };
  }

  function messageFor(r, inCart) {
    var text;
    if (r.state === "soldout") return MESSAGES.soldout;
    if (r.state === "preorder") text = MESSAGES.preorder;
    else if (r.limit === 1) text = "Only 1 unit of this product is in stock.";
    else text = "Only " + r.limit + " units of this product are in stock.";
    if (inCart > 0) text += " You already have " + inCart + " in your cart.";
    return text;
  }

  function productScope(el) {
    if (!el || !el.closest) return document;
    return el.closest("[data-product-wrapper]") || el.closest(".shopify-section") || document;
  }

  function formFor(el) {
    if (!el) return null;
    if (el.form) return el.form;
    var formId = el.getAttribute && (el.getAttribute("form") || el.getAttribute("data-form"));
    if (formId && document.getElementById(formId)) return document.getElementById(formId);
    var scope = productScope(el);
    return scope.querySelector('form[action*="/cart/add"]');
  }

  function inventoryFor(form) {
    var holders = [form.closest("[data-product-wrapper]"), form.closest(".shopify-section")];
    for (var i = 0; i < holders.length; i++) {
      var el = holders[i] && holders[i].querySelector('[type="application/json"][data-name="main-product-inventories"]');
      if (!el) continue;
      if (!inventoryCache.has(el)) {
        var map = {};
        try {
          JSON.parse(el.textContent).forEach(function (v) {
            map[String(v.id)] = v;
          });
        } catch (e) {}
        inventoryCache.set(el, map);
      }
      return inventoryCache.get(el);
    }
    return null;
  }

  function ruleFromInventory(v) {
    var qty = parseInt(v.inventory_quantity, 10) || 0;
    if (v.inventory_policy === "continue" && qty <= 0) return rule("preorder", PREORDER_LIMIT);
    if (v.inventory_management !== "shopify") return rule("untracked", Infinity);
    if (qty > 0) return rule("stock", qty);
    return rule("soldout", 0);
  }

  /* Collection cards have no inventory JSON: the button tells pre-order / sold out apart, and
     for in-stock cards Shopify itself refuses quantities above stock ("Deny" policy). */
  function ruleFromButton(form, trigger) {
    var btn = trigger && trigger.hasAttribute && trigger.hasAttribute("data-add-to-cart") ? trigger : null;
    /* getAttribute: form.id is the form's <input name="id"> (variant), not its id attribute. */
    var formId = form.getAttribute("id");
    if (!btn && formId) btn = document.querySelector('[data-add-to-cart][form="' + formId + '"]');
    if (!btn) btn = form.querySelector("[data-add-to-cart]");
    if (btn && btn.hasAttribute("data-sold-out")) return rule("soldout", 0);
    if (btn && btn.getAttribute("data-nj-preorder") === "pre-order") return rule("preorder", PREORDER_LIMIT);
    return rule("stock", Infinity);
  }

  function variantRule(form, variantId, trigger) {
    var inventory = inventoryFor(form);
    if (inventory && inventory[String(variantId)]) return ruleFromInventory(inventory[String(variantId)]);
    return ruleFromButton(form, trigger);
  }

  /* Cart drawer / cart page line: limit and type rendered by Liquid (no attribute = no limit). */
  function ruleFromLine(input) {
    if (!input || !input.hasAttribute("data-nj-qty-max")) return null;
    var limit = parseInt(input.getAttribute("data-nj-qty-max"), 10);
    return rule(input.getAttribute("data-nj-qty-type") === "preorder" ? "preorder" : "stock", limit);
  }

  function selectedVariantId(form) {
    var input = form && form.querySelector('[name="id"]');
    return input ? input.value : null;
  }

  /* ---------- PDP message ----------
     Desktop renders one warning under the quantity selector ("quantity", snippets/product-blocks.liquid)
     and one under the buttons ("buttons", snippets/product-form-buttons.liquid), ~500px apart. Only the
     one next to the control the customer used is shown; mobile has just the "buttons" one. */

  /* A warning is only useful where its container is on screen (CSS hides the desktop selector on phones). */
  function canShow(el) {
    return !!el && !!el.parentElement && el.parentElement.getClientRects().length > 0;
  }

  function messageTarget(scope, source) {
    var all = Array.from(scope.querySelectorAll(MSG_SELECTOR));
    var preferred = scope.querySelector(MSG_SELECTOR.slice(0, -1) + '="' + source + '"]');
    if (canShow(preferred)) return preferred;
    return all.filter(canShow)[0] || preferred || all[0] || scope.querySelector("[data-form-error]");
  }

  function clearMessage(scope) {
    Array.from((scope || document).querySelectorAll(MSG_SELECTOR)).forEach(function (el) {
      if (el.hidden) return;
      el.textContent = "";
      el.hidden = true;
    });
  }

  function showMessage(scope, text, source) {
    scope = scope || document;
    var el = messageTarget(scope, source || "buttons");
    if (!el) return;
    Array.from(scope.querySelectorAll(MSG_SELECTOR)).forEach(function (other) {
      if (other !== el) other.hidden = true;
    });
    el.textContent = text;
    el.classList.remove("hidden");
    el.hidden = false;
    /* Phones: keep the warning right above the fixed buttons bar. */
    var bar = el.previousElementSibling;
    var onBar = !!bar && bar.classList.contains("pdp-action-wrapper") && getComputedStyle(bar).position === "fixed";
    el.classList.toggle("nj-qty-limit-msg--bar", onBar);
    el.style.bottom = onBar ? bar.offsetHeight + "px" : "";
  }

  document.addEventListener("click", function (event) {
    var el = event.target.closest && event.target.closest(MSG_SELECTOR);
    if (el) el.hidden = true;
  });

  /* ---------- quantity selector ---------- */

  function limitForInput(input) {
    var form = formFor(input);
    var variantId = selectedVariantId(form);
    if (!form || !variantId) return null;
    var r = variantRule(form, variantId);
    r.scope = productScope(form);
    return r;
  }

  /* true when `value` is within the limit (and clears the warning); otherwise warns and returns false.
     `source` picks where the warning shows: "quantity" (default) or "buttons" (Buy Now). */
  function checkQuantity(input, value, source) {
    var info = limitForInput(input);
    if (!info) return true;
    value = parseInt(value, 10) || 1;
    if (value <= info.limit) {
      clearMessage(info.scope);
      return true;
    }
    showMessage(info.scope, messageFor(info, 0), source || "quantity");
    return false;
  }

  function pdpQuantityInputs(scope) {
    return Array.from(scope.querySelectorAll("[data-quantity-input]")).filter(function (input) {
      return !input.classList.contains("ajax-cart-update") && input.type !== "hidden";
    });
  }

  /* Typed quantities are clamped to the limit (capture phase, before theme.js copies the value). */
  document.addEventListener(
    "input",
    function (event) {
      var input = event.target;
      if (!input.matches || !input.matches("[data-quantity-input]") || input.classList.contains("ajax-cart-update")) return;
      var info = limitForInput(input);
      if (!info || input.value === "") return;
      var value = parseInt(input.value, 10);
      if (value > Math.max(info.limit, 1)) {
        input.value = Math.max(info.limit, 1);
        showMessage(info.scope, messageFor(info, 0), "quantity");
      } else {
        clearMessage(info.scope);
      }
    },
    true
  );

  /* Variant switch: apply the new variant's limit to the selector without changing a valid quantity. */
  function refresh(scope) {
    scope = scope || document;
    var inputs = pdpQuantityInputs(scope);
    var info = inputs.length ? limitForInput(inputs[0]) : null;
    if (!info) return;
    var max = Math.max(info.limit, 1);
    var clamped = false;
    inputs.forEach(function (input) {
      if (isFinite(max)) input.setAttribute("max", max);
      else input.removeAttribute("max");
      if ((parseInt(input.value, 10) || 1) > max) {
        input.value = max;
        clamped = true;
      }
    });
    if (clamped && info.limit > 0) showMessage(info.scope, messageFor(info, 0), "quantity");
    else clearMessage(info.scope);
  }

  /* ---------- add to cart ---------- */

  function requestedQuantity(form) {
    var values = new FormData(form).getAll("quantity").map(function (v) {
      return parseInt(v, 10) || 0;
    });
    return values.length ? Math.max.apply(null, values) || 1 : 1;
  }

  /*
    Core check: rejects when the variant is sold out, or when cart quantity + requested
    quantity would exceed the limit. `proceed` runs inside the lock and should return the
    add request's promise so the next queued mutation sees the updated cart.
  */
  function guardVariant(variantId, quantity, r, proceed, onReject) {
    return withLock(function () {
      if (r.limit === 0) {
        onReject(messageFor(r, 0));
        return false;
      }
      if (quantity > r.limit) {
        onReject(messageFor(r, 0));
        return false;
      }
      if (!isFinite(r.limit)) return proceed();
      return getCart().then(
        function (cart) {
          var inCart = quantityInCart(cart, variantId, -1);
          if (inCart + quantity > r.limit) {
            onReject(messageFor(r, inCart));
            return false;
          }
          return proceed();
        },
        function () {
          onReject(MESSAGES.verify);
          return false;
        }
      );
    });
  }

  function guardAdd(form, trigger, formParent, proceed) {
    var variantId = selectedVariantId(form);
    if (!variantId) return Promise.resolve(proceed());
    var scope = formParent || productScope(form);
    var r = variantRule(form, variantId, trigger);
    return guardVariant(variantId, requestedQuantity(form), r, function () {
      clearMessage(scope);
      return proceed();
    }, function (text) {
      showMessage(scope, text, "buttons");
      /* cart.js disabled the button for the request; a sold-out variant's button stays disabled. */
      if (trigger && r.state !== "soldout") trigger.removeAttribute("disabled");
    });
  }

  /* ---------- cart drawer / cart page lines ---------- */

  function rejectLine(cartItem, text) {
    var error = cartItem.querySelector(".cart-item-error");
    var errorText = cartItem.querySelector("[data-error-text]");
    if (error && errorText) {
      errorText.textContent = text;
      error.style.display = "block";
    }
    Array.from(cartItem.querySelectorAll(".quantity-input")).forEach(function (input) {
      input.value = input.getAttribute("data-previous-value");
    });
    cartItem.classList.remove("disabled");
  }

  function guardChange(line, quantity, cartItem, proceed) {
    var r = ruleFromLine(cartItem && cartItem.querySelector("[data-nj-qty-max]"));
    if (!r || !(quantity > 0)) return Promise.resolve(proceed());
    return withLock(function () {
      return getCart().then(
        function (cart) {
          var index = parseInt(line, 10) - 1;
          var item = cart.items && cart.items[index];
          if (!item) return proceed();
          var others = quantityInCart(cart, item.variant_id, index);
          if (others + quantity > r.limit) {
            rejectLine(cartItem, messageFor(r, others));
            return false;
          }
          return proceed();
        },
        function () {
          rejectLine(cartItem, MESSAGES.verify);
          return false;
        }
      );
    });
  }

  /* Breeze cart checkout: first line whose variant total is over its limit, as a message (or null). */
  function cartViolation(cart) {
    var rules = {};
    Array.from(document.querySelectorAll("[data-nj-qty-max][data-line]")).forEach(function (input) {
      rules[input.getAttribute("data-line")] = ruleFromLine(input);
    });
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var r = rules[String(i + 1)];
      if (!r || !(r.limit >= 0)) continue;
      if (quantityInCart(cart, items[i].variant_id, -1) > r.limit) {
        return items[i].product_title + ": " + messageFor(r, 0);
      }
    }
    return null;
  }

  /* ---------- backstop for native form posts ---------- */

  /* If a product form is ever submitted natively (cart.js failed to load, or another script
     submits it), check it here first and only then let it through. */
  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (event.defaultPrevented || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    if (form.__njQtyApproved) {
      form.__njQtyApproved = false;
      return;
    }
    event.preventDefault();
    var submitter = event.submitter && event.submitter.form === form ? event.submitter : undefined;
    guardAdd(form, submitter, null, function () {
      form.__njQtyApproved = true;
      if (form.requestSubmit) form.requestSubmit(submitter);
      else form.submit();
    });
  });

  window.NJQtyLimit = {
    PREORDER_LIMIT: PREORDER_LIMIT,
    guardAdd: guardAdd,
    guardVariant: guardVariant,
    guardChange: guardChange,
    checkQuantity: checkQuantity,
    cartViolation: cartViolation,
    refresh: refresh,
  };

  document.addEventListener("DOMContentLoaded", function () {
    Array.from(document.querySelectorAll("[data-product-wrapper]")).forEach(refresh);
  });
})();
